import React from 'react';
import numbro from 'numbro';
import rest from '@data/restWrapper';
import { setModalContent, closeModal } from '@components/modal';
import { loadCheckoutInvoice, applyCurrentOffer, undoLastOffer } from './controler';
import ConfirmButtons from '@components/buttons/confirmButtons';
import { condoStatusCheck, checkOfferMismatch } from '@pages/newReservations/utils';
import dayjs from 'dayjs';
import {
    CheckoutOptionsProps,
    ExchangeRates,
    CurrentOfferProps,
    InvoiceTypeEnum,
} from './interfaces';
import {
    InvoiceItemsModel,
    PriceOfferIntervalsGroupByReservationId,
    ReservationEditModel,
    ReservationModelWithModelsIncluded,
} from '@common/modelDefinition';
import { stopGlobalLoading } from '@components/loaders/globalLoading';
import { toast } from 'react-toastify';
import { translate } from '@data/translations';
import useStore from '@data/state/zustand';

const CurrentOffer = (props: CheckoutOptionsProps) => {
    const {
        invoice,
        invoiceItems: proformaInvoiceItems,
        reservation,
        groupReservationsArray,
        proformaInvoices,
        genuineInvoices,
        posTerminalInvoices = [],
    } = props;

    const { pendingInvoice, invoiceType, terminalType, reservationId, groupUuid, id, connectedInvoices } = invoice;
    const [invoiceItems, setInvoiceItems] = React.useState<InvoiceItemsModel[]>([]);
    const [priceOfferIntervals, setPriceOfferIntervals] = React.useState<PriceOfferIntervalsGroupByReservationId>({});
    
    // Dependencies za useEffect
    const childrenNumber = reservation?.childrenNumber || 0;
    const guestTaxConfig = reservation?.guestTaxConfig || null;
    const groupGuestConfigs = groupReservationsArray?.map(r => 
        JSON.stringify(r.guestTaxConfig || null)
    ).join(',') || '';
    
 React.useEffect(() => {
    const loadOffer = async () => {       
        const { invoiceItems: items, priceOfferByLinkedIntervals } = await rest('/getAccommodationOffer', 'POST', {
            reservationId,
            groupUuid,
        });

        setInvoiceItems(items);
        setPriceOfferIntervals(priceOfferByLinkedIntervals);
    };
    
    if (reservationId || groupUuid) {
        loadOffer().catch((err) => {
            console.warn(err);
        });
    }
}, [reservationId, groupUuid, childrenNumber, guestTaxConfig, groupGuestConfigs]);

    const targetReservations: ReservationEditModel[] | ReservationModelWithModelsIncluded[] = reservation
        ? [reservation]
        : groupReservationsArray;

    let showPreviousOffer: boolean = false;
    for (const res of targetReservations) {
        const { rememberedAccommodationPriceInfo } = res;
        if (rememberedAccommodationPriceInfo && rememberedAccommodationPriceInfo.length > 1) {
            showPreviousOffer = true;
        }
    }
const simpleViewEnabled = useStore((appState: any) => appState.state.checkoutInvoice.simpleViewEnabled);

    if (
        terminalType !== 'reservation' ||
        !pendingInvoice ||
        invoiceType === InvoiceTypeEnum.advance ||
        id === undefined ||
        simpleViewEnabled
    ) {
        return null;
    }
    
    let nonCondoReservations: ReservationEditModel[] = [];
    for (const targetReservation of targetReservations) {
        const { isCondoReservation } = condoStatusCheck(targetReservation);
        if (!isCondoReservation) {
            nonCondoReservations.push(targetReservation);
        }
    }

    const accommodationProformasFromReservation = proformaInvoices.filter((i) => {
        const { InvoiceItems } = i;
        const accommodationInvoiceItems = InvoiceItems.filter((it) => it.type === 'accommodation');
        if (accommodationInvoiceItems.length > 0 && i.reclamation === 0 && !i.connectedInvoiceProcessed) {
            return i;
        } else {
            return null;
        }
    });

    // NOVO: Provjeri i posTerminalInvoices koji imaju accommodation (npr. splitani računi)
    const accommodationProformasFromPosTerminal = posTerminalInvoices.filter((i) => {
        const { InvoiceItems } = i;
        const accommodationInvoiceItems = InvoiceItems.filter((it) => it.type === 'accommodation');
        if (accommodationInvoiceItems.length > 0 && i.reclamation === 0 && !i.connectedInvoiceProcessed) {
            return i;
        } else {
            return null;
        }
    });

    const accommodationGenuineInvoices = genuineInvoices?.filter((i) => {
        const { InvoiceItems } = i;
        const accommodationInvoiceItems = InvoiceItems?.filter((it) => it.type === 'accommodation');
        if (accommodationInvoiceItems && accommodationInvoiceItems.length > 0 && i.reclamation === 0) {
            return i;
        } else {
            return null;
        }
    }) || [];

    const offerMismatch: string[] =
        nonCondoReservations.length > 0
            ? checkOfferMismatch({
                  invoiceItems,
                  proformaInvoiceItems,
                  reservations: nonCondoReservations,
              })
            : [];

    // Izračunaj razliku u cijeni
    const currentTotal = invoiceItems
        .filter(i => i.type === 'accommodation')
        .reduce((sum, i) => sum + (i.bruttoValueWithDiscount || 0), 0);
    
    const proformaTotal = proformaInvoiceItems
        .filter(i => i.type === 'accommodation')
        .reduce((sum, i) => sum + (i.bruttoValueWithDiscount || 0), 0);
    
    const priceDifference = currentTotal - proformaTotal;

    const currentOfferProps: CurrentOfferProps & { reservation?: any; priceDifference?: number; proformaTotal?: number; currentTotal?: number } = {
        invoiceItems: invoiceItems,
        invoice: invoice,
        translate: translate,
        priceOfferIntervals: priceOfferIntervals,
        reservationId,
        groupUuid,
        reservation,
        priceDifference,
        proformaTotal,
        currentTotal,
    };
    const hasConnectedInvoices = connectedInvoices && connectedInvoices.length > 0;
  
    return (
        <div className="mt-16p">
            {accommodationProformasFromReservation.length === 0 && accommodationProformasFromPosTerminal.length === 0 && !hasConnectedInvoices && accommodationGenuineInvoices.length === 0 ? (
                offerMismatch.length > 0 ? (
                    <div>
                        <AccommodationOffer {...currentOfferProps} />
                        <div className="display-flex justify-content-end mt-4p">
                            <PriceOfferButton {...currentOfferProps} />
                        </div>
                    </div>
                ) : showPreviousOffer ? null : null
            ) : null}
        </div>
    );
};

// ============================================================================
// HELPER: Parse itemName i vrati čitljiv tekst
// ============================================================================
const parseAccommodationItemName = (itemName: string, locale: string): string => {
    const parts = itemName.split(':');
    const itemType = parts[0];
    
    let displayName = '';
    let fromDate = '';
    let toDate = '';
    
    if (itemType === 'AccommodationAdults') {
        // Format: AccommodationAdults:count:fromDate:toDate
        const count = parts[1];
        fromDate = parts[2];
        toDate = parts[3];
        displayName = `${translate('Accommodation')} (${count} ${translate('adults')})`;
    } else if (itemType === 'AccommodationChild') {
        // Format: AccommodationChild:ageRange:count:fromDate:toDate
        const ageRange = parts[1];
        const count = parts[2];
        fromDate = parts[3];
        toDate = parts[4];
        displayName = `${translate('Accommodation')} (${translate('child')} ${ageRange}${translate('yrs')}, ${count}x)`;
    } else if (itemType === 'AccommodationCapped') {
        // Format: AccommodationCapped:count:fromDate:toDate
        const count = parts[1];
        fromDate = parts[2];
        toDate = parts[3];
        displayName = `${translate('Accommodation')} (${count} ${translate('persons')})`;
    } else if (itemType === 'Accommodation') {
        // Format: Accommodation:fromDate:toDate
        fromDate = parts[1];
        toDate = parts[2];
        displayName = translate('Accommodation');
    } else {
        return itemName;
    }
    
    // Format datuma
    const fromFormatted = fromDate ? dayjs(fromDate).locale(locale).format('ll') : '';
    const toFormatted = toDate ? dayjs(toDate).locale(locale).format('ll') : '';
    
    const dateDisplay = fromDate === toDate || !toDate
        ? fromFormatted
        : `${fromFormatted} - ${toFormatted}`;
    
    return `${displayName}: ${dateDisplay}`;
};

// ============================================================================
// ACCOMMODATION OFFER - s prikazom razlike
// ============================================================================
const AccommodationOffer = (props: CurrentOfferProps & { 
    reservation?: any; 
    priceDifference?: number; 
    proformaTotal?: number; 
    currentTotal?: number;
}) => {
    const { priceDifference = 0, proformaTotal = 0, currentTotal = 0, invoice } = props;
    const { currency } = invoice;
    
    const isIncrease = priceDifference > 0;
    // const isDecrease = priceDifference < 0;
    
    return (
        <div className="">
            <div className="mb-8p p-8p" style={{ 
                backgroundColor: '#fff3cd', 
                borderRadius: '4px',
                border: '1px solid #ffc107',
            }}>
                <div className="display-flex align-items-center justify-content-between flex-wrap">
                    <div>
                        <b>{translate('Price change detected')}</b>
                    </div>
                    <div className="display-flex align-items-center" style={{ gap: '16px' }}>
                        <div>
                            <span style={{ color: '#6c757d' }}>{translate('Current')}:</span>{' '}
                            <span style={{ textDecoration: 'line-through' }}>
                                {numbro(proformaTotal).format({ mantissa: 2, thousandSeparated: true })} {currency}
                            </span>
                        </div>
                        <div>
                            <span style={{ color: '#6c757d' }}>{translate('New')}:</span>{' '}
                            <b>{numbro(currentTotal).format({ mantissa: 2, thousandSeparated: true })} {currency}</b>
                        </div>
                        {priceDifference !== 0 && (
                            <div style={{ 
                                color: isIncrease ? '#dc3545' : '#28a745',
                                fontWeight: 'bold',
                            }}>
                                ({isIncrease ? '+' : ''}{numbro(priceDifference).format({ mantissa: 2, thousandSeparated: true })} {currency})
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="mt-8p">
                <small className="text-muted">{translate('New price breakdown')}:</small>
            </div>
            <InvoiceItemsWithHeader {...props} />
        </div>
    );
};

// ============================================================================
// PRICE OFFER BUTTON
// ============================================================================
interface PriceOfferButtonProps extends CurrentOfferProps {
    reservation?: any;
}

const PriceOfferButton = (props: PriceOfferButtonProps) => {
    const { priceOfferIntervals, undo = false, reservationId, groupUuid } = props;
    
    return (
        <div
            onClick={() => {
                setModalContent(
                    <div className="container-fluid">
                        <div className="text-center">
                            {translate('Please confirm.')}
                            <hr />
                        </div>

                        <div className="text-center">
                            <ConfirmButtons
                                onConfirm={async () => {
                                    try {
                                        if (!undo && priceOfferIntervals) {
                                            await applyCurrentOffer({
                                                reservationId,
                                                groupUuid,
                                                priceOfferIntervals,
                                            });
                                        } else {
                                            await undoLastOffer({
                                                reservationId,
                                                groupUuid,
                                            });
                                        }

                                        await loadCheckoutInvoice({ reservationId, groupUuid });
                                        closeModal();
                                    } catch (error: any) {
                                        stopGlobalLoading();
                                        toast(translate(error.message), { type: toast.TYPE.ERROR });
                                        closeModal();
                                    }
                                }}
                                onCancel={() => {
                                    closeModal();
                                }}
                            />
                        </div>
                    </div>,

                    translate(!undo ? 'Apply new price' : 'Undo last offer'),
                    false,
                    'modal-md'
                );
            }}
            className={!undo ? 'button-primary' : 'button-primary'}
        >
            {translate(!undo ? 'Apply new price' : 'Undo last offer')}
        </div>
    );
};

// ============================================================================
// HELPER: Group invoice items by group
// ============================================================================
export const getGroupedInvoiceItemsByGroup = (invoiceItems: InvoiceItemsModel[]) => {
    return invoiceItems.reduce((groups, item) => {
        const key = item.group === null ? 'null' : item.group;
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
        return groups;
    }, {} as Record<string, typeof invoiceItems>);
};

// ============================================================================
// INVOICE ITEMS WITH HEADER
// ============================================================================
const InvoiceItemsWithHeader = (props: CurrentOfferProps & { reservation?: any }) => {
    const { invoiceItems = [], invoice } = props;
    const { exchangeRates, currency, locale } = invoice;

    const exchangeRatesWhenInvoiceIssued: ExchangeRates = exchangeRates ? JSON.parse(exchangeRates) : {};
    const currencyExchangeRate: number = exchangeRatesWhenInvoiceIssued[currency]
        ? Number(exchangeRatesWhenInvoiceIssued[currency])
        : 1;

    const groupedInvoiceItems = getGroupedInvoiceItemsByGroup(invoiceItems);

    if (!groupedInvoiceItems) {
        return null;
    }
    return (
        <div className="">
            <InvoiceItemsHeader {...props} />

            {Object.keys(groupedInvoiceItems).map((group: string, key: number) => {
                return (
                    <ul key={key} className="list-group list-group-flush mt-0 border-bottom border-top">
                        {group !== 'undefined' && group !== 'null' && (
                            <li className="list-group-item p-1 bg-light">
                                <small>
                                    <div className="text-left">{group}</div>
                                </small>
                            </li>
                        )}
                        {groupedInvoiceItems[group].map((i: InvoiceItemsModel, index: number) => {
                            // Parsiraj itemName u čitljiv format
                            const displayName = i.type === 'accommodation' 
                                ? parseAccommodationItemName(i.itemName, locale)
                                : i.itemName;
                            
                            return (
                                <li 
                                    key={index} 
                                    className="list-group-item p-1"
                                >
                                    <small>
                                        <div className="row d-flex justify-content-between">
                                            <div className="col-4 col-sm-4">
                                                {displayName}
                                            </div>
                                            <div className="col-4 col-sm-2">
                                                {numbro(i.quantity).format({ mantissa: 2, trimMantissa: true })}
                                            </div>

                                            <div className="col-4 col-sm-2 text-center">
                                                {i.bruttoUnitPrice
                                                    ? numbro(i.bruttoUnitPrice * Number(currencyExchangeRate)).format({
                                                          mantissa: 2,
                                                          trimMantissa: false,
                                                          thousandSeparated: true,
                                                      })
                                                    : ''}
                                            </div>

                                            <div className="col-4 col-sm-2 text-right">
                                                {i.tax1ValueWithDiscount
                                                    ? numbro(
                                                          i.tax1ValueWithDiscount * Number(currencyExchangeRate)
                                                      ).format({
                                                          mantissa: 2,
                                                          trimMantissa: false,
                                                          thousandSeparated: true,
                                                      })
                                                    : ''}
                                            </div>

                                            <div className="col-4 col-sm-2 text-right">
                                                {i.bruttoValueWithDiscount
                                                    ? numbro(
                                                          i.bruttoValueWithDiscount * Number(currencyExchangeRate)
                                                      ).format({
                                                          mantissa: 2,
                                                          trimMantissa: false,
                                                          thousandSeparated: true,
                                                      })
                                                    : ''}
                                            </div>
                                        </div>
                                    </small>
                                </li>
                            );
                        })}
                    </ul>
                );
            })}
        </div>
    );
};

// ============================================================================
// INVOICE ITEMS HEADER
// ============================================================================
const InvoiceItemsHeader = (props: CurrentOfferProps & { reservation?: any }) => {
    return (
        <ul className="list-group list-group-flush mt-2 border-top">
            <li className="list-group-item p-1">
                <div className="row d-flex justify-content-between">
                    <div className="col-4 col-sm-4">
                        <b> {translate('Name')} </b>
                    </div>
                    <div className="col-4 col-sm-2">
                        <b> {translate('Quantity')} </b>
                    </div>

                    <div className="col-4 col-sm-2 text-center">
                        <b> {translate('Retail price')} </b>
                    </div>

                    <div className="col-4 col-sm-2 text-right">
                        <b> {translate('Tax')} </b>
                    </div>

                    <div className="col-4 col-sm-2 text-right">
                        <b> {translate('Amount')} </b>
                    </div>
                </div>
            </li>
        </ul>
    );
};

export default CurrentOffer;